/** @jsxImportSource @emotion/react */

import { LayerControlButtons } from '../LayerControlButtons'
import { Box, Typography, useTheme } from '@mui/material'
import { accordionHazards } from '..'
import { hiddenLayerContainer, hiddenLayerLabel, layerHeader } from './LayerAccordionList.styles'
import { MapLayer, MapLayerType } from '@app/redux/riskMap/riskMapSlice'
import { DialogLayersPreviewProps } from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog/LayersPreview'
import { Accordion } from '@app/components/Atoms/Accordion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SortableList } from '@app/components/Atoms/Sortable'
import { usePreferences } from '@app/hooks/usePreferences'
import { LayerDetailsReport } from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDetailsFetcher'

export const LayerAccordionList = function LayerAccordList({
  layers,
  layerDetailsList,
  handleUpdateLayer,
  dialogProps,
  isDialogPreview,
  handleDeleteLayer,
}: {
  layers: MapLayer[]
  layerDetailsList: LayerDetailsReport[][] | (LayerDetailsReport[][] | null)[] | null
  handleUpdateLayer?: ({
    parentLayerId,
    newLayers,
  }: {
    parentLayerId: MapLayerType
    newLayers: MapLayer[]
  }) => void
  dialogProps?: DialogLayersPreviewProps
  isDialogPreview: boolean
  handleDeleteLayer?: (props: { selectedLayer: MapLayer }) => void
}) {
  const theme = useTheme()
  const { use_hazard_name } = usePreferences()

  const parentLayerId = useMemo(() => layers[0].layerType, [layers])
  const triggeredUpdate = useRef(false)

  const [items, setItems] = useState<MapLayer[]>([])

  useEffect(() => {
    if (triggeredUpdate.current) {
      triggeredUpdate.current = false
      return
    }
    setItems(
      layers.map((layer) => {
        return { ...layer, id: layer.id ?? layer.tilesets.map((tileset) => tileset.id).join('-') }
      }),
    )
  }, [layers])

  const renderItem = (layer: MapLayer) => {
    const layerDetails = layerDetailsList
      ? (layerDetailsList.find(
          (detailReport) =>
            Array.isArray(detailReport) &&
            detailReport.some(
              (detail) =>
                detail &&
                !Array.isArray(detail) &&
                (detail.layer_id === layer.type ||
                  detail.layer_id === layer.assetTag ||
                  detail.layer_id === layer.id.replace('urbanintelligence.', '')),
            ),
        ) as LayerDetailsReport[] | null)
      : null

    const accordion = (
      <Accordion
        css={accordionHazards({ theme })}
        defaultExpanded={false}
        lazyContent={false}
        variant={'outline'}
        level={'h3'}
        key={layer.id}
        isDraggable={!!handleUpdateLayer}
        accordionStateKey={undefined}
        header={
          <Box css={layerHeader({ canExpand: false })}>
            <Box css={hiddenLayerContainer({ theme })}>
              <Typography
                css={hiddenLayerLabel({
                  theme,
                  isVisible: layer.visible,
                })}
              >
                {use_hazard_name.flag
                  ? (layer.hazard_name ?? layer.display_name)
                  : layer.display_name}
              </Typography>
            </Box>

            <LayerControlButtons
              layer={layer}
              layerDetails={layerDetails}
              dialogProps={dialogProps}
              isDialogPreview={isDialogPreview}
              handleDeleteLayer={handleDeleteLayer}
            />
          </Box>
        }
        body={undefined}
      />
    )

    if (!handleUpdateLayer) {
      return accordion
    }

    return (
      <SortableList.Item key={layer.id} id={layer.id}>
        {accordion}
      </SortableList.Item>
    )
  }

  if (!handleUpdateLayer) {
    return (
      <Box css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        {items.map((layer) => renderItem(layer))}
      </Box>
    )
  }

  return (
    <SortableList
      items={items}
      onChange={(newLayers) => {
        triggeredUpdate.current = true
        setItems(newLayers)
        handleUpdateLayer({ parentLayerId, newLayers })
      }}
      renderItem={(layer, _, _isDragging) => {
        const layerDetails = layerDetailsList
          ? (layerDetailsList.find(
              (detailReport) =>
                Array.isArray(detailReport) &&
                detailReport.some(
                  (detail) =>
                    detail &&
                    !Array.isArray(detail) &&
                    (detail.layer_id === layer.type ||
                      detail.layer_id === layer.assetTag ||
                      detail.layer_id === layer.id.replace('urbanintelligence.', '')),
                ),
            ) as LayerDetailsReport[] | null)
          : null
        /*
        const canExpand =
          (layerDetails && layerDetails.length > 0 && !isDialogPreview) ||
          (!!layer.filterable_information && !isDialogPreview && !!layerDetails) ||
          false */

        return (
          <SortableList.Item key={layer.id} id={layer.id}>
            <Accordion
              css={accordionHazards({ theme })}
              defaultExpanded={false}
              lazyContent={false}
              variant={'outline'}
              level={'h3'}
              isDraggable
              accordionStateKey={undefined}
              header={
                <Box css={layerHeader({ canExpand: false })}>
                  <Box css={hiddenLayerContainer({ theme })}>
                    <Typography
                      css={hiddenLayerLabel({
                        theme,
                        isVisible: layer.visible,
                      })}
                    >
                      {use_hazard_name.flag
                        ? (layer.hazard_name ?? layer.display_name)
                        : layer.display_name}
                    </Typography>
                  </Box>

                  <LayerControlButtons
                    layer={layer}
                    layerDetails={layerDetails}
                    dialogProps={dialogProps}
                    isDialogPreview={isDialogPreview}
                    handleDeleteLayer={handleDeleteLayer}
                  />
                </Box>
              }
              body={undefined}
            />
          </SortableList.Item>
        )
      }}
    />
  )
}
